import { Inject, Injectable } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import {
  AccountGroup,
  AccountGroupApiService,
  ApiLookupFilter,
  ApiTrialFilter,
  LookupFilter,
  ProjectionFilter,
  SortDirection,
  SortField,
} from '@galaxy/account-group';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { MarketsService } from '../../core/markets/markets.service';
import { User } from '../../core/user';
import { UserService } from '../../users';
import { AccountGroupAssociationsService } from '../account-group-associations.service';
import {
  ActionTypes,
  Init,
  LoadAccountGroups,
  LoadAccountGroupsError,
  LoadAccountGroupsSuccess,
  LoadMoreAccountGroups,
  LoadUser,
  LoadUserError,
  LoadUserSuccess,
  RemoveAssociation,
  RemoveAssociationError,
  RemoveAssociationSuccess,
  Search,
  ToggleAll,
  ToggleRow,
  UpdatePagination,
  UpdateProductPermissions,
  UpdateTabPermissions,
} from './account-group-permissions.actions';
import { accountGroupPermissionsReducer, initialState, State } from './account-group-permissions.reducers';

@Injectable({
  providedIn: 'root',
})
export class AccountGroupPermissionsService {
  private readonly store = new BehaviorSubject<State>({ ...initialState });

  private get state(): State {
    return this.store.getValue();
  }
  private set state(newState: State) {
    this.store.next(newState);
  }

  public readonly total$: Observable<number> = this.store.pipe(
    map((state) => state.totalResults),
    distinctUntilChanged(),
  );
  public readonly searchTerm$: Observable<string> = this.store.pipe(
    map((state) => state.search),
    distinctUntilChanged(),
  );
  public readonly pageSize$: Observable<number> = this.store.pipe(
    map((state) => state.pageSize),
    distinctUntilChanged(),
  );
  public readonly pageIndex$: Observable<number> = this.store.pipe(
    map((state) => state.pageIndex),
    distinctUntilChanged(),
  );
  public readonly error$: Observable<any> = this.store.pipe(
    map((state) => state.error),
    distinctUntilChanged(),
  );
  public readonly loadingUser$: Observable<boolean> = this.store.pipe(
    map((state) => state.loadingUser),
    distinctUntilChanged(),
  );
  public readonly user$: Observable<User | null> = this.store.pipe(
    map((state) => state.user),
    distinctUntilChanged(),
  );
  public readonly loadingAccountGroups$: Observable<boolean> = this.store.pipe(
    map((state) => state.loadingAccountGroups),
    distinctUntilChanged(),
  );
  public readonly removingAssociations$: Observable<string[]> = this.store.pipe(
    map((state) => state.removingAssociations),
    distinctUntilChanged(),
  );
  public readonly removingAssociationsErrors$: Observable<string[]> = this.store.pipe(
    map((state) => state.removingAssociationsErrors),
    distinctUntilChanged(),
  );
  public readonly associatedAccountGroups$: Observable<AccountGroup[]> = this.store.pipe(
    map((state) => state.associatedAccountGroups),
    distinctUntilChanged(),
  );
  public readonly associatedAccountGroupIds$: Observable<string[]> = this.user$.pipe(
    map((user) => (user ? user.accountGroupAssociations : null)),
    distinctUntilChanged(),
  );
  public readonly selected$: Observable<string[]> = this.store.pipe(
    map((state) => state.selectedAccountGroupIds),
    distinctUntilChanged(),
  );
  public readonly isAllSelected: Observable<boolean> = combineLatest([
    this.associatedAccountGroups$,
    this.selected$,
  ]).pipe(
    map(([all, selected]) => {
      if (!all) {
        return false;
      }
      return all.length <= selected.length && all.length > 0;
    }),
    distinctUntilChanged(),
  );
  public readonly isAnySelected: Observable<boolean> = this.selected$.pipe(
    map((selected) => selected.length > 0),
    distinctUntilChanged(),
  );
  public readonly accountTabPermissions$: Observable<{
    [key: string]: string[];
  }> = this.user$.pipe(
    map((user) => (user ? user.businessCenterTabs : null)),
    distinctUntilChanged(),
  );
  public readonly accountAccessPermissions$: Observable<{
    [key: string]: string[];
  }> = this.user$.pipe(
    map((user) => (user ? user.accountAccessPermissions : null)),
    distinctUntilChanged(),
  );
  public readonly associatedAccountGroupsPage$: Observable<AccountGroup[]> = combineLatest([
    this.associatedAccountGroups$,
    this.pageIndex$,
    this.pageSize$,
  ]).pipe(
    map(([agids, pageIndex, pageSize]) =>
      agids ? agids.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize) : [],
    ),
    distinctUntilChanged(),
  );
  public readonly noBusinesses$: Observable<boolean> = combineLatest([
    this.loadingUser$,
    this.loadingAccountGroups$,
    this.associatedAccountGroups$,
  ]).pipe(
    map(
      ([loadingUser, loadingAccountGroups, associatedAccountGroups]) =>
        !(loadingUser || loadingAccountGroups) && associatedAccountGroups && associatedAccountGroups.length === 0,
    ),
    distinctUntilChanged(),
  );

  constructor(
    private userService: UserService,
    private associationsService: AccountGroupAssociationsService,
    private accountGroupApiService: AccountGroupApiService,
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private marketsService: MarketsService,
    private snackbarService: SnackbarService,
  ) {}

  updateSearch(searchTerm: string): void {
    const action = new Search({ searchTerm });
    this.state = accountGroupPermissionsReducer(this.state, action);
    this.state = accountGroupPermissionsReducer(this.state, new LoadAccountGroups({}));
    this.loadAccountGroups();
  }

  init(): void {
    const action = new Init();
    this.state = accountGroupPermissionsReducer(this.state, action);
  }

  loadUser(userId: string): void {
    const action = new LoadUser({ userId });
    this.state = accountGroupPermissionsReducer(this.state, action);
    this.userService
      .getUser$(userId, ['STRUCT_ATTRIBUTES', 'USER_ID'])
      .pipe(
        map((user) => new LoadUserSuccess({ user })),
        take(1),
        catchError((err) => of(new LoadUserError({ error: err }))),
      )
      .subscribe((thisAction) => {
        this.state = accountGroupPermissionsReducer(this.state, thisAction);
        if (thisAction.type === ActionTypes.LOAD_USER_SUCCESS) {
          this.state = accountGroupPermissionsReducer(this.state, new LoadAccountGroups({}));
          this.loadAccountGroups();
        }
      });
  }

  removeAssociation(accountGroupId: string): void {
    const action = new RemoveAssociation({ accountGroupId });
    this.state = accountGroupPermissionsReducer(this.state, action);
    this.associationsService
      .deleteAssociation$(this.state.userId, accountGroupId)
      .pipe(
        map(() => new RemoveAssociationSuccess({ accountGroupId })),
        catchError((err) => of(new RemoveAssociationError({ accountGroupId, error: err }))),
      )
      .subscribe((thisAction) => {
        this.state = accountGroupPermissionsReducer(this.state, thisAction);
        if (thisAction.type === ActionTypes.REMOVE_ASSOCIATION_SUCCESS) {
          this.snackbarService.successSnack(`Access was removed.`);
        }
      });
  }

  removeSelectedAssociatons(): void {
    this.state.selectedAccountGroupIds.map((accountGroupId) => this.removeAssociation(accountGroupId));
  }

  updateProductPermissions(accountGroupId: string, permissions: string[]): void {
    const action = new UpdateProductPermissions({
      accountGroupId,
      permissions,
    });
    this.state = accountGroupPermissionsReducer(this.state, action);
  }

  updateTabPermissions(accountGroupId: string, tabIds: string[]): void {
    const action = new UpdateTabPermissions({ accountGroupId, tabIds });
    this.state = accountGroupPermissionsReducer(this.state, action);
  }

  updatePagination(event: PageEvent): void {
    const action = new UpdatePagination(event);
    this.state = accountGroupPermissionsReducer(this.state, action);
    if (!this.state.associatedAccountGroups) {
      this.state = accountGroupPermissionsReducer(this.state, new LoadAccountGroups({}));
      this.loadAccountGroups();
    } else if (
      (this.state.pageIndex + 1) * this.state.pageSize > this.state.associatedAccountGroups.length &&
      this.state.hasMore
    ) {
      this.state = accountGroupPermissionsReducer(this.state, new LoadMoreAccountGroups({}));
      this.loadAccountGroups();
    }
  }

  toggleAll(): void {
    const action = new ToggleAll();
    this.state = accountGroupPermissionsReducer(this.state, action);
  }

  toggleRow(accountGroupId: string): void {
    const action = new ToggleRow({ accountGroupId });
    this.state = accountGroupPermissionsReducer(this.state, action);
  }

  private loadAccountGroups(): void {
    const accountGroupIds = this.state.user.accountGroupAssociations;
    if (!accountGroupIds || accountGroupIds.length === 0) {
      const action = new LoadAccountGroupsSuccess({
        accountGroups: [],
        cursor: null,
        hasMore: false,
        totalResults: 0,
      });
      this.state = accountGroupPermissionsReducer(this.state, action);
      return;
    }

    combineLatest([
      this.partnerId$.pipe(
        filter((partner) => !!partner),
        take(1),
      ),
      this.marketsService.markets.pipe(map((markets) => markets.map((market) => market.market_id), take(1))),
    ])
      .pipe(
        switchMap(([partnerId, markets]) => {
          const projectionFilters = new ProjectionFilter({
            accounts: true,
            napData: true,
            accountGroupExternalIdentifiers: true,
          });
          const filters = new LookupFilter();
          filters.accountGroupIds = accountGroupIds;

          const apiFilters = new ApiLookupFilter(
            partnerId,
            markets,
            filters.salesPersonId,
            filters.tags,
            filters.accountFilters,
            filters.listingDistributionFilter,
            filters.listingSyncProFilter,
            filters.createdDateFilter,
            new ApiTrialFilter(false),
            filters.taxonomyIds,
            filters.presenceFilter,
            filters.locationFilter,
            filters.snapshotFilter,
            filters.statusFilter,
            filters.accountGroupIds,
          );

          return this.accountGroupApiService
            .lookup(projectionFilters, apiFilters, this.state.cursor, this.state.pageSize, this.state.search, {
              field: SortField.Created,
              direction: SortDirection.Descending,
            })
            .pipe(
              map(
                (r) =>
                  new LoadAccountGroupsSuccess({
                    accountGroups: r.accountGroups,
                    cursor: r.nextCursor,
                    hasMore: r.hasMore,
                    totalResults: r.totalResults,
                  }),
              ),
              catchError((err) => of(new LoadAccountGroupsError({ error: err }))),
            );
        }),
      )
      .subscribe((action) => (this.state = accountGroupPermissionsReducer(this.state, action)));
  }
}
